export interface PseaCoreStandard {
  number: number;
  title: string;
  description?: string;
  options: string[];
  /**
   * Whether "Not applicable" is offered alongside Yes/No. Only standards that
   * can genuinely not apply to an organization carry it — sub-contracting
   * clauses (2) and corrective action for allegations (8), which are moot for
   * an organization that sub-contracts nothing or has had no allegations.
   * It scores the same point as "Yes" so a standard that cannot apply does not
   * drag the rating down.
   */
  allowNotApplicable?: boolean;
}

export const OTHER_SPECIFY_OPTION = "Other (please specify)";

/**
 * The 8 PSEA Core Standards used to structure the PSEA Assessment form and
 * read-only views. Each standard has a title, a descriptive prompt and a list
 * of evidence options (checkboxes). Where "Other (please specify)" is present a
 * free-text field is shown for the respondent to elaborate.
 */
export const PSEA_CORE_STANDARDS: PseaCoreStandard[] = [
  {
    number: 1,
    title: "Organizational Policy",
    description:
      "The partner has a policy document on PSEA. At a minimum, this document should include a written undertaking that the partner accepts the standards of conduct listed in section 3 of the ST/SGB/2003/13",
    options: [
      "Code of Conduct (internal or interagency)",
      "PSEA policy",
      "Documentation of standard procedures for all personnel to receive/sign PSEA policy",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 2,
    title: "Organizational Management",
    description:
      "Your organization’s contracts and partnership agreements include a standard clause requiring sub-contractors to adopt policies that prohibit SEA and to take measures to prevent and respond to SEA.",
    options: [
      "PSEA Contracts/partnership agreements for sub-contractors",
      OTHER_SPECIFY_OPTION,
    ],
    allowNotApplicable: true,
  },
  {
    number: 3,
    title: "Human Resource Systems",
    description:
      "There is a systematic vetting procedure in place for job candidates through proper screening. This must include, at minimum, reference checks for sexual misconduct and a self-declaration by the job candidate, confirming that they have never been subject to sanctions (disciplinary, administrative or criminal) arising from an investigation in relation to SEA, or left employment pending investigation and refused to cooperate in such an investigation.",
    options: [
      "Reference check template including check for sexual misconduct (including reference from previous employers and self-declaration)",
      "Recruitment procedures",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 4,
    title: "Mandatory Training",
    description:
      // Line breaks are meaningful here: the three requirements are a list in
      // the source document, and the description is rendered with pre-wrap so
      // they stay on separate lines.
      "The partner holds mandatory trainings (online or in-person) for all IP employees and associated personnel (herein \u201cpersonnel\u201d) on PSEA and relevant procedures.\n" +
      "The training should, at a minimum include:\n" +
      "1) a definition of SEA (that is aligned with the UN's definition);\n" +
      "2) an explanation on prohibition of SEA; and\n" +
      "3) actions that personnel are required to take (i.e. prompt reporting of allegations and referral of victims).",
    options: [
      "Annual training plan",
      "Training agenda",
      "Training package",
      "Attendance sheets",
      "Training certificates",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 5,
    title: "Reporting",
    description:
      "The partner has mechanisms and procedures for personnel, recipients of assistance and communities, including children, to report SEA allegations that comply with core standards for reporting (i.e. safety, confidentiality, transparency, accessibility).",
    options: [
      "Internal Complaints and Feedback Mechanism",
      "Participation in joint reporting mechanisms",
      "Communication materials",
      "PSEA awareness raising plan",
      "Description of reporting mechanism",
      "Whistle-blower policy",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 6,
    title: "Assistance and Referrals",
    description:
      "The partner has a system to refer SEA victims to locally available support services, based on their needs and consent. This can include actively contributing to in-country PSEA networks and/or GBV systems (where applicable) and/or referral pathways at an inter-agency level.",
    options: [
      "Internal or Interagency referral pathway",
      "List of available service providers",
      "Description of referral procedures or Standard Operation Procedure (SOP)",
      "Referral form for survivors/victims of GBV or SEA",
      "Guidelines on victim assistance and/or training on GBV and GBV case management principles",
    ],
  },
  {
    number: 7,
    title: "Investigations",
    description:
      "The organization has a process for investigation of allegations of SEA and can provide evidence. This may include a referral system for investigations where in-house capacity does not exist.",
    options: [
      "Written process for review of SEA allegations",
      "Dedicated resources for investigation(s) and/or commitment of partner for support",
      "PSEA investigation policy/procedures",
      "Contract with professional investigative service",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 8,
    title: "Corrective Action",
    description:
      "The partner has taken appropriate corrective action in response to SEA allegations, if any.",
    options: [
      "Evidence of implementation of corrective measures identified by the UN partner entity, including capacity strengthening of staff",
      "Specific measures to identify and reduce risks of SEA in programme delivery",
      OTHER_SPECIFY_OPTION,
    ],
    allowNotApplicable: true,
  },
];
